import type { ReactElement } from "react";
import { defineHFComponent } from "../component.js";

export type TitlecardCalmProps = {
  headline?: string;
  kicker?: string;
  duration?: number;
};

function View({ headline = "Less, but better", kicker = "DESIGN PRINCIPLE" }: TitlecardCalmProps): ReactElement {
  return <div className="tc-clip"><div className="tc-lockup"><span className="tc-kicker">{kicker}</span><span className="tc-headline">{headline}</span></div></div>;
}

export const TitlecardCalm = defineHFComponent<TitlecardCalmProps>({
  name: "titlecard-calm",
  View,
  css: (scope) => `
    ${scope}{position:absolute;inset:0;overflow:hidden;container-type:size;isolation:isolate;color:var(--fg,#f7f7f2);font-family:var(--font-display,"Helvetica Neue",Helvetica,Arial,sans-serif);pointer-events:none}
    ${scope} .tc-clip{position:absolute;inset:0;display:grid;place-items:center;overflow:hidden}
    ${scope} .tc-lockup{display:flex;width:78cqw;flex-direction:column;align-items:flex-start;gap:6cqh;transform-origin:50% 50%;will-change:transform,opacity}
    ${scope} .tc-kicker{display:block;width:100%;color:var(--muted,#b8c0cc);font-family:var(--font-mono,ui-monospace,"SF Mono",Menlo,Consolas,monospace);font-size:3.2cqmin;font-weight:600;line-height:1;letter-spacing:.18em;text-transform:uppercase;will-change:transform,opacity}
    ${scope} .tc-headline{display:block;width:100%;color:var(--fg,#f7f7f2);font-family:var(--font-display,"Helvetica Neue",Helvetica,Arial,sans-serif);font-size:17cqmin;font-weight:600;line-height:.94;letter-spacing:-.055em;overflow-wrap:anywhere;text-wrap:balance;will-change:transform,opacity}
  `,
  timeline: ({ tl, select }, { duration = 4 }) => {
    const resolvedDuration = Math.max(0.001, duration);
    const inBase = 1.2;
    const outBase = 0.55;
    const phaseScale = resolvedDuration < inBase + outBase ? resolvedDuration / (inBase + outBase) : 1;
    const intro = inBase * phaseScale;
    const outro = outBase * phaseScale;
    const hold = Math.max(0, resolvedDuration - intro - outro);
    tl.fromTo(select(" .tc-kicker"), { opacity: 0, y: "2.2cqh" }, { opacity: 1, y: "0cqh", duration: 0.5 * phaseScale, ease: "power2.out" }, 0);
    tl.fromTo(select(" .tc-headline"), { opacity: 0, y: "3.2cqh" }, { opacity: 1, y: "0cqh", duration: 0.72 * phaseScale, ease: "power3.out" }, 0.28 * phaseScale);
    if (hold > 0) tl.to(select(" .tc-lockup"), { y: "-0.65cqh", duration: hold, ease: "sine.inOut" }, intro);
    tl.to(select(" .tc-lockup"), { opacity: 0, y: "-2.1cqh", duration: outro, ease: "power2.in" }, intro + hold);
  },
});
